'use client';

import { useCallback, useEffect, useRef } from 'react';
import { useEditor, EditorContent, type JSONContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import { Box, IconButton, Divider, Paper, useTheme } from '@mui/material';
import {
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  FormatListBulleted,
  FormatListNumbered,
  Link as LinkIcon,
  LinkOff,
} from '@mui/icons-material';
import {
  tiptapToPortableText,
  portableTextToTiptap,
  type PortableTextContent,
} from './portableText';

export interface RichTextEditorProps {
  value?: PortableTextContent;
  onChange?: (value: PortableTextContent) => void;
  placeholder?: string;
  isDisabled?: boolean;
  minHeight?: number | string;
}

export const RichTextEditor = ({
  value,
  onChange,
  placeholder = 'Start typing...',
  isDisabled = false,
  minHeight = 200,
}: RichTextEditorProps) => {
  const theme = useTheme();
  const isInternalChange = useRef(false);

  const getInitialContent = () => {
    if (!value) return undefined;
    try {
      return portableTextToTiptap(value);
    } catch (err) {
      console.error('Failed to convert initial portable text:', err);
      return undefined;
    }
  };

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
      }),
      Underline,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          rel: 'noopener noreferrer',
          target: '_blank',
        },
      }),
      Placeholder.configure({
        placeholder,
      }),
    ],
    content: getInitialContent(),
    editable: !isDisabled,
    onUpdate: ({ editor }) => {
      if (onChange) {
        isInternalChange.current = true;
        const json = editor.getJSON();
        const portableText = tiptapToPortableText(json);
        onChange(portableText);
      }
    },
  });

  useEffect(() => {
    if (isInternalChange.current) {
      isInternalChange.current = false;
      return;
    }
    if (editor && value) {
      try {
        const tiptapContent = portableTextToTiptap(value);
        editor.commands.setContent(tiptapContent);
      } catch (err) {
        console.error('Failed to convert portable text to tiptap:', err);
      }
    }
  }, [editor, value]);

  useEffect(() => {
    if (editor) {
      editor.setEditable(!isDisabled);
    }
  }, [editor, isDisabled]);

  const setLink = useCallback(() => {
    if (!editor) return;
    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt('URL', previousUrl);
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  }, [editor]);

  const unsetLink = useCallback(() => {
    if (!editor) return;
    editor.chain().focus().unsetLink().run();
  }, [editor]);

  if (!editor) return null;

  return (
    <Paper
      variant='outlined'
      sx={{
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        opacity: isDisabled ? 0.6 : 1,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          px: 1,
          py: 0.5,
          borderBottom: `1px solid ${theme.palette.divider}`,
          flexWrap: 'wrap',
        }}
      >
        <IconButton
          size='small'
          onClick={() => editor.chain().focus().toggleBold().run()}
          disabled={isDisabled}
          color={editor.isActive('bold') ? 'primary' : 'default'}
          aria-label='Bold'
        >
          <FormatBold fontSize='small' />
        </IconButton>
        <IconButton
          size='small'
          onClick={() => editor.chain().focus().toggleItalic().run()}
          disabled={isDisabled}
          color={editor.isActive('italic') ? 'primary' : 'default'}
          aria-label='Italic'
        >
          <FormatItalic fontSize='small' />
        </IconButton>
        <IconButton
          size='small'
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          disabled={isDisabled}
          color={editor.isActive('underline') ? 'primary' : 'default'}
          aria-label='Underline'
        >
          <FormatUnderlined fontSize='small' />
        </IconButton>

        <Divider orientation='vertical' flexItem sx={{ mx: 0.5 }} />

        <IconButton
          size='small'
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          disabled={isDisabled}
          color={editor.isActive('bulletList') ? 'primary' : 'default'}
          aria-label='Bullet list'
        >
          <FormatListBulleted fontSize='small' />
        </IconButton>
        <IconButton
          size='small'
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          disabled={isDisabled}
          color={editor.isActive('orderedList') ? 'primary' : 'default'}
          aria-label='Numbered list'
        >
          <FormatListNumbered fontSize='small' />
        </IconButton>

        <Divider orientation='vertical' flexItem sx={{ mx: 0.5 }} />

        <IconButton
          size='small'
          onClick={setLink}
          disabled={isDisabled}
          color={editor.isActive('link') ? 'primary' : 'default'}
          aria-label='Add link'
        >
          <LinkIcon
            sx={{
              fontSize: 'small',
            }}
          />
        </IconButton>
        {editor.isActive('link') && (
          <IconButton
            size='small'
            onClick={unsetLink}
            disabled={isDisabled}
            aria-label='Remove link'
          >
            <LinkOff fontSize='small' />
          </IconButton>
        )}
      </Box>

      <Box
        sx={{
          minHeight,
          p: 2,
          '& .ProseMirror': {
            outline: 'none',
            minHeight:
              typeof minHeight === 'number' ? minHeight - 32 : minHeight,
            '& p': {
              margin: 0,
              marginBottom: 1,
            },
            '& p.is-editor-empty:first-child::before': {
              color: theme.palette.text.disabled,
              content: 'attr(data-placeholder)',
              float: 'left',
              height: 0,
              pointerEvents: 'none',
            },
            '& h1, & h2, & h3': {
              marginTop: 2,
              marginBottom: 1,
            },
            '& ul, & ol': {
              paddingLeft: 3,
              marginBottom: 1,
            },
            '& li': {
              marginBottom: 0.5,
            },
            '& a': {
              color: theme.palette.primary.main,
              textDecoration: 'underline',
            },
          },
        }}
      >
        <EditorContent editor={editor} />
      </Box>
    </Paper>
  );
};

RichTextEditor.displayName = 'RichTextEditor';
export default RichTextEditor;
